import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, HeartHandshake, Sparkles } from "lucide-react";
import { MagicLetterBoxLink } from "@/components/funnel/MagicLetterBoxLink";
import {
  ELF_EMERGENCY_GUIDES,
  getEmergencyGuidePath,
} from "@/data/elfEmergencyGuides";
import { buildGuideSchema } from "@/data/elfEmergencySeo";
import useSEO from "@/hooks/useSEO";
import { getCanonicalUrl } from "@/utils/seoUtils";
import NotFound from "@/pages/NotFound";
import { elfLetterOccasionUrl } from "@/data/elfLetterOccasions";

const ElfEmergencyGuide = () => {
  const { slug } = useParams<{ slug: string }>();
  const guide = ELF_EMERGENCY_GUIDES.find((candidate) => candidate.slug === slug && !candidate.existingArticlePath);
  const seoGuide = guide ?? ELF_EMERGENCY_GUIDES[0];
  const canonicalUrl = getCanonicalUrl(`/elf-help/${seoGuide.slug}`);
  const schema = useMemo(
    () => buildGuideSchema(seoGuide, canonicalUrl),
    [canonicalUrl, seoGuide],
  );

  useSEO({
    title: `${seoGuide.title} | Elf Letter Bot`,
    description: seoGuide.description,
    canonicalUrl,
    schema,
    noIndex: !guide,
  });

  if (!guide) {
    return <NotFound />;
  }

  const { content } = guide;
  const siblingGuides = ELF_EMERGENCY_GUIDES.filter(
    (candidate) => candidate.slug !== guide.slug,
  );

  return (
    <div className="bg-[#fffaf7] text-foreground">
      <article aria-labelledby="guide-title" className="overflow-hidden">
        <section className="bg-elfGreen-light">
          <div className="container mx-auto max-w-5xl px-4 py-6 md:py-10">
            <nav aria-label="Breadcrumb" className="mb-8 text-sm text-elfGreen-ink">
              <ol className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <li>
                  <Link
                    to="/"
                    className="rounded-sm underline decoration-elfGreen-dark/50 underline-offset-4 hover:text-elfGreen focus:outline-none focus-visible:ring-2 focus-visible:ring-elfGreen-dark"
                  >
                    Home
                  </Link>
                </li>
                <li aria-hidden="true">/</li>
                <li>
                  <Link to="/elf-help/" className="underline underline-offset-4">Elf help</Link>
                </li>
                <li aria-hidden="true">/</li>
                <li aria-current="page" className="font-medium text-foreground">
                  {guide.title}
                </li>
              </ol>
            </nav>

            <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-end">
              <div className="max-w-3xl">
                <p className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/75 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-elfGreen-ink">
                  <Sparkles aria-hidden="true" className="h-3.5 w-3.5" />
                  Quick elf help
                </p>
                <h1
                  id="guide-title"
                  className="text-3xl font-bold leading-tight text-foreground md:text-5xl"
                >
                  {guide.title}
                </h1>
                <p className="mt-5 max-w-2xl text-base leading-7 text-foreground/80 md:text-lg">
                  {content.introduction}
                </p>
              </div>

              <div className="rounded-2xl border border-white/80 bg-white/70 p-5 shadow-sm">
                <p className="text-sm font-semibold uppercase tracking-[0.14em] text-elfRed-ink">
                  Need a note now?
                </p>
                <p className="mt-2 text-sm leading-6 text-foreground/80">
                  Create a personalized letter that fits your child and this exact moment.
                </p>
                <MagicLetterBoxLink
                  source={`elf-help/${guide.slug}`}
                  campaign="emergency-guide"
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-elfRed-ink px-4 py-3 text-center text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#892638] focus:outline-none focus-visible:ring-2 focus-visible:ring-elfRed-dark focus-visible:ring-offset-2"
                >
                  Create this letter for your child
                  <ArrowRight aria-hidden="true" className="h-4 w-4" />
                </MagicLetterBoxLink>
                <p className="mt-3 text-xs leading-5 text-foreground/75">
                  Opens The Magic Letter Box. Start with a five-letter parent trial; choose a paid Family membership for unlimited letters.
                </p>
              </div>
            </div>
          </div>
        </section>

        <div className="container mx-auto max-w-4xl px-4 py-10 md:py-16">
          <section
            aria-labelledby="direct-answer"
            className="rounded-2xl border-l-4 border-elfRed bg-white p-6 shadow-sm md:p-8"
          >
            <p className="text-sm font-semibold uppercase tracking-[0.14em] text-elfRed-ink">
              The short answer
            </p>
            <h2 id="direct-answer" className="mt-2 text-2xl font-bold text-elfGreen-ink md:text-3xl">
              You can keep the magic moving
            </h2>
            <p className="mt-4 text-base leading-7 text-foreground/85 md:text-lg">
              {content.directAnswer}
            </p>
          </section>

          <section aria-labelledby="options-heading" className="mt-14">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-[0.14em] text-elfGreen-ink">
                Choose what fits tonight
              </p>
              <h2 id="options-heading" className="mt-2 text-2xl font-bold md:text-3xl">
                Three practical ways forward
              </h2>
            </div>

            <div className="mt-7 grid gap-5 md:grid-cols-3">
              {content.options.map((option, index) => (
                <section
                  key={option.title}
                  className="flex h-full flex-col rounded-2xl border border-elfGreen/30 bg-white p-6 shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-elfGreen-light text-sm font-bold text-elfGreen-ink">
                      {index + 1}
                    </span>
                    <h3 className="text-lg font-bold leading-snug text-elfGreen-ink">
                      {option.title}
                    </h3>
                  </div>
                  <p className="mt-4 text-sm leading-6 text-foreground/80">{option.description}</p>
                  <ol className="mt-5 space-y-3 border-t border-elfGreen/20 pt-5 text-sm leading-6 text-foreground/80">
                    {option.steps.map((step) => (
                      <li key={step} className="flex gap-2">
                        <CheckCircle2
                          aria-hidden="true"
                          className="mt-1 h-4 w-4 shrink-0 text-elfGreen-ink"
                        />
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </section>
              ))}
            </div>
          </section>

          <section aria-labelledby="example-heading" className="mt-14">
            <div className="rounded-2xl bg-elfYellow-light/60 p-6 md:p-8">
              <div className="flex items-start gap-3">
                <HeartHandshake aria-hidden="true" className="mt-1 h-6 w-6 shrink-0 text-elfRed-ink" />
                <div>
                  <h2 id="example-heading" className="text-2xl font-bold md:text-3xl">
                    A short note you can use
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-foreground/75">
                    Copy this as written or adjust the elf name and one detail to make it yours.
                  </p>
                </div>
              </div>
              <blockquote className="mt-6 rounded-xl border border-elfYellow-dark/40 bg-white px-5 py-5 text-base italic leading-7 text-foreground shadow-sm md:px-7 md:text-lg">
                “{content.copyableExample}”
              </blockquote>
              <MagicLetterBoxLink
                source={`elf-help/${guide.slug}`}
                campaign="emergency-guide-example"
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-elfRed-ink px-5 py-3 text-center text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#892638] focus:outline-none focus-visible:ring-2 focus-visible:ring-elfRed-dark focus-visible:ring-offset-2"
              >
                Create this letter for your child
                <ArrowRight aria-hidden="true" className="h-4 w-4" />
              </MagicLetterBoxLink>
              <p className="mt-4 text-sm leading-6 text-slate-700">
                <Link
                  to={elfLetterOccasionUrl(guide.slug === "forgot-to-move" ? "forgot-to-move" : "arrival")}
                  className="rounded-sm font-semibold text-elfGreen-ink underline underline-offset-4 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-700 focus-visible:ring-offset-2"
                >
                  Try the free {guide.slug === "forgot-to-move" ? "forgot-to-move" : "welcome-back"} letter starter
                </Link>
                . Includes three PDF deliveries per email address.
              </p>
            </div>
          </section>

          <section aria-labelledby="scenario-heading" className="mt-14 rounded-2xl border border-elfPink/50 bg-elfPink-light/30 p-6 md:p-8">
            <p className="text-sm font-semibold uppercase tracking-[0.14em] text-elfRed-ink">
              Fictional family example
            </p>
            <h2 id="scenario-heading" className="mt-2 text-2xl font-bold md:text-3xl">
              {content.scenario.title}
            </h2>
            <p className="mt-4 text-base leading-7 text-foreground/85">{content.scenario.body}</p>
          </section>

          <section aria-labelledby="about-author" className="mt-14 border-t border-elfGreen/25 pt-8">
            <h2 id="about-author" className="text-xl font-bold text-elfGreen-ink">
              From the Elf Letter Team
            </h2>
            <p className="mt-3 text-sm leading-6 text-foreground/80">
              This guide is published by{" "}
              <Link
                to="/about#editorial"
                className="font-semibold text-elfGreen-ink underline underline-offset-4 hover:text-elfGreen focus:outline-none focus-visible:ring-2 focus-visible:ring-elfGreen-dark"
              >
                Elf Letter Team
              </Link>
              . Donnie, founder of Elf Letter Bot and The Magic Letter Box, publishes the site and stewards the relationship between the two products.
            </p>
          </section>

          <section aria-labelledby="related-heading" className="mt-14">
            <h2 id="related-heading" className="text-2xl font-bold md:text-3xl">
              Keep exploring
            </h2>
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              <div className="rounded-2xl bg-elfGreen-light/60 p-6">
                <h3 className="text-lg font-bold text-elfGreen-ink">More elf help</h3>
                <ul className="mt-4 space-y-3 text-sm leading-6">
                  {siblingGuides.map((sibling) => (
                    <li key={sibling.slug}>
                      <Link
                        to={getEmergencyGuidePath(sibling)}
                        className="inline-flex items-start gap-2 font-semibold text-elfGreen-ink underline decoration-elfGreen-dark/40 underline-offset-4 hover:text-elfGreen focus:outline-none focus-visible:ring-2 focus-visible:ring-elfGreen-dark"
                      >
                        <ArrowRight aria-hidden="true" className="mt-1 h-4 w-4 shrink-0" />
                        <span>{sibling.title}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-elfGreen/20">
                <h3 className="text-lg font-bold text-elfGreen-ink">Plan the next moment</h3>
                <ul className="mt-4 space-y-3 text-sm leading-6">
                  {guide.relatedLinks.map((related) => (
                    <li key={related.href}>
                      <Link
                        to={related.href}
                        className="inline-flex items-start gap-2 font-semibold text-elfGreen-ink underline decoration-elfGreen-dark/40 underline-offset-4 hover:text-elfGreen focus:outline-none focus-visible:ring-2 focus-visible:ring-elfGreen-dark"
                      >
                        <ArrowRight aria-hidden="true" className="mt-1 h-4 w-4 shrink-0" />
                        <span>{related.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          <section className="mt-14 rounded-2xl bg-elfGreen-ink px-6 py-8 text-center text-white md:px-10 md:py-10">
            <h2 className="text-2xl font-bold md:text-3xl">Ready to make this moment personal?</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-white/85 md:text-base">
              Create a letter that uses your child’s name, your elf’s voice, and the details that make your family’s tradition feel like yours.
            </p>
            <MagicLetterBoxLink
              source={`elf-help/${guide.slug}`}
              campaign="emergency-guide-footer"
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-elfRed-ink px-5 py-3 text-center text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#892638] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-elfGreen-dark"
            >
              Create this letter for your child
              <ArrowRight aria-hidden="true" className="h-4 w-4" />
            </MagicLetterBoxLink>
          </section>
        </div>
      </article>
    </div>
  );
};

export default ElfEmergencyGuide;
